import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { getTravelReport } from "../../server/Api";
import {
  ReportPageShell,
  exportRowsToExcel,
  formatDate,
  safeFileLabel,
  yesNo,
} from "./reportShared";
import "../../assets/css/occasion.css";
import "../../assets/css/StayReports.css";
import "../../assets/css/FestivaReports.css";

const TravelReport = () => {
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadReport = useCallback(async () => {
    if (!selectedEvent?.eventId) {
      setReport(null);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(getTravelReport(selectedEvent.eventId));
      setReport(res.data?.data || null);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to load travel report"
      );
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent?.eventId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const rows = useMemo(() => {
    const list = report?.rows || [];
    if (!searchTerm.trim()) return list;
    const q = searchTerm.toLowerCase();
    return list.filter((row) =>
      [
        row.name,
        row.guestName,
        row.arrivalMode,
        row.departureMode,
        row.arrivalFlightTrainBusNo,
        row.departureFlightTrainBusNo,
        row.pickupLocation,
        row.dropLocation,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [report, searchTerm]);

  const handleExport = () => {
    if (!rows.length) {
      toast.error("No travel rows to export.");
      return;
    }
    const eventLabel = safeFileLabel(selectedEvent?.eventName, "Event");
    exportRowsToExcel({
      rows,
      sheetName: "Travel Plan",
      fileName: `${eventLabel}_Travel_Report.xlsx`,
      reportTitle: "Travel Report — Arrivals, Departures & Transfers",
      eventName: selectedEvent?.eventName || "",
      columns: [
        { label: "Person Type", value: (r) => r.personType || "", width: 12 },
        {
          label: "Guest / Attendee Name",
          value: (r) => r.name || "",
          width: 24,
        },
        {
          label: "Main Guest",
          value: (r) =>
            r.personType === "Attendee" ? r.guestName || "" : r.name || "",
          width: 22,
        },
        {
          label: "Arrival Date",
          value: (r) => formatDate(r.arrivalDate, ""),
          width: 12,
        },
        { label: "Arrival Time", value: (r) => r.arrivalTime || "", width: 12 },
        {
          label: "Arrival Mode",
          value: (r) => r.arrivalMode || "",
          width: 14,
        },
        {
          label: "Arrival Flight / Train / Bus No.",
          value: (r) => r.arrivalFlightTrainBusNo || "",
          width: 22,
        },
        {
          label: "Arrival PNR",
          value: (r) => r.arrivalPNRNo || "",
          width: 14,
        },
        {
          label: "Airport / Station Pickup Needed",
          value: (r) => yesNo(r.pickupRequired),
          width: 16,
        },
        {
          label: "Pickup Location",
          value: (r) => r.pickupLocation || "",
          width: 20,
        },
        {
          label: "Pickup Vehicle",
          value: (r) => r.pickupVehicle || "",
          width: 16,
        },
        {
          label: "Departure Date",
          value: (r) => formatDate(r.departureDate, ""),
          width: 12,
        },
        {
          label: "Departure Time",
          value: (r) => r.departureTime || "",
          width: 12,
        },
        {
          label: "Departure Mode",
          value: (r) => r.departureMode || "",
          width: 14,
        },
        {
          label: "Departure Flight / Train / Bus No.",
          value: (r) => r.departureFlightTrainBusNo || "",
          width: 22,
        },
        {
          label: "Drop to Airport / Station Needed",
          value: (r) => yesNo(r.dropRequired),
          width: 16,
        },
        {
          label: "Drop Location",
          value: (r) => r.dropLocation || "",
          width: 20,
        },
        {
          label: "Drop Vehicle",
          value: (r) => r.dropVehicle || "",
          width: 16,
        },
        {
          label: "Local Transport Vehicle",
          value: (r) => r.localVehicle || "",
          width: 18,
        },
      ],
    });
    toast.success("Export successful!");
  };

  const summary = report?.summary;

  return (
    <ReportPageShell
      title="Travel Report"
      subtitle="Arrival, departure, pickup/drop, and local transport for logistics planning."
      hasEvent={Boolean(selectedEvent?.eventId)}
      loading={loading}
      onRefresh={loadReport}
      onExport={handleExport}
      exportDisabled={!report}
      metrics={[
        { label: "Travel Records", value: summary?.totalRecords ?? 0 },
        {
          label: "With Arrival",
          value: summary?.withArrival ?? 0,
          tone: "is-covered",
        },
        {
          label: "With Departure",
          value: summary?.withDeparture ?? 0,
          tone: "is-covered",
        },
        {
          label: "Pickup Needed",
          value: summary?.pickupRequired ?? 0,
          tone: "is-occupied",
        },
        {
          label: "Drop Needed",
          value: summary?.dropRequired ?? 0,
          tone: "is-occupied",
        },
        {
          label: "No Travel Yet",
          value: summary?.guestsWithoutTravel ?? 0,
          tone: "is-gap",
          hint: `of ${summary?.guestsCovered ?? 0} with travel`,
        },
      ]}
      toolbar={
        <div className="stay-report-toolbar">
          <input
            type="search"
            className="form-control stay-report-search"
            placeholder="Search guest, flight, location…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      }
    >
      <section className="stay-report-section">
        <div className="stay-report-table-wrap">
          <div className="stay-report-table-head">
            <h3>Travel movements</h3>
            <span>{rows.length} records</span>
          </div>
          <div className="stay-report-table-scroll">
            <table className="table stay-report-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Arrival</th>
                  <th>Pickup</th>
                  <th>Departure</th>
                  <th>Drop</th>
                  <th>Local</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? (
                  rows.map((row) => (
                    <tr key={row.travelId}>
                      <td>
                        <strong>{row.name}</strong>
                        <div className="festiva-report-muted">
                          {row.personType}
                          {row.personType === "Attendee"
                            ? ` · ${row.guestName}`
                            : ""}
                        </div>
                      </td>
                      <td>
                        <div>
                          {formatDate(row.arrivalDate)}{" "}
                          {row.arrivalTime || ""}
                        </div>
                        <div className="festiva-report-muted">
                          {[row.arrivalMode, row.arrivalFlightTrainBusNo]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </div>
                      </td>
                      <td>
                        {row.pickupRequired ? (
                          <>
                            <span className="stay-report-status is-occupied">
                              Required
                            </span>
                            <div className="festiva-report-muted">
                              {row.pickupLocation || "—"}
                            </div>
                            {row.pickupVehicle ? (
                              <div className="festiva-report-muted">
                                {row.pickupVehicle}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <div>
                          {formatDate(row.departureDate)}{" "}
                          {row.departureTime || ""}
                        </div>
                        <div className="festiva-report-muted">
                          {[row.departureMode, row.departureFlightTrainBusNo]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </div>
                      </td>
                      <td>
                        {row.dropRequired ? (
                          <>
                            <span className="stay-report-status is-occupied">
                              Required
                            </span>
                            <div className="festiva-report-muted">
                              {row.dropLocation || "—"}
                            </div>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{row.localVehicle || "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No travel records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </ReportPageShell>
  );
};

export default TravelReport;
